/**
 * StreamingVoiceClient.js
 * Production-grade Web Audio API + Realtime WebSocket Voice Streaming Client.
 * 
 * Capabilities:
 * - Direct navigator.mediaDevices.getUserMedia({ audio: ... }) with permission handling
 * - AudioWorkletNode capturing 16kHz 16-bit Linear PCM frames
 * - AnalyserNode FFT & waveform live feeding for UI audio visualizer
 * - Real-time binary WebSocket streaming to Node.js backend voice gateway
 * - Zero background microphone drain when inactive (mic turns off after interaction)
 * - Server-side Sarvam VAD events (vad.speech_start, vad.speech_end)
 * - Realtime partial transcripts as the user speaks
 * - Instant Barge-in: stops active TTS playback when user starts speaking
 * - Web Audio API buffer playback for synthesized voice synced with visualizer
 */

import { io } from "socket.io-client";
import { PCM_PROCESSOR_CODE } from "./pcmProcessorCode";
import { getBackendBaseUrl } from "../utils/apiUrl";

const BACKEND_URL = getBackendBaseUrl();

export class StreamingVoiceClient {
  constructor(options = {}) {
    this.options = options;
    this.onStateChange = options.onStateChange || (() => {});
    this.onVolumeChange = options.onVolumeChange || (() => {});
    this.onTranscriptPartial = options.onTranscriptPartial || (() => {});
    this.onTranscriptFinal = options.onTranscriptFinal || (() => {});
    this.onResult = options.onResult || (() => {});
    this.onError = options.onError || (() => {});
    this.onSpeechStart = options.onSpeechStart || (() => {});
    this.onSpeechEnd = options.onSpeechEnd || (() => {});

    // Audio graph
    this.audioContext = null;
    this.mediaStream = null;
    this.sourceNode = null;
    this.workletNode = null;
    this.scriptProcessorNode = null;
    this.analyserNode = null;
    this.playbackSourceNode = null;

    // WebSocket
    this.socket = null;
    this.isConnected = false;

    // State management
    this.isStreaming = false;
    this.currentStreamId = null;
    this.language = options.language || "auto";

    // Safety & Low-latency dynamic VAD silence detection
    this.maxRecordingTimer = null;
    this.silenceTimer = null;
    this.partialSilenceTimer = null;
    this.hasSpoken = false;
    this.ambientNoise = 0.006;
    this.lastSpokenTimestamp = 0;
    this.latestPartialText = "";

    // Volume monitoring animation loop
    this.animFrameId = null;
    this.fftDataArray = null;
    this.timeDomainArray = null;

    // Initialize socket connection
    this._initSocket();
  }

  _initSocket() {
    if (this.socket) return;

    this.socket = io(BACKEND_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });

    this.socket.on("connect", () => {
      this.isConnected = true;
      console.log("[StreamingVoiceClient] Connected to Voice WebSocket gateway:", this.socket.id);
    });

    this.socket.on("disconnect", () => {
      this.isConnected = false;
      console.log("[StreamingVoiceClient] Disconnected from Voice WebSocket gateway");
    });

    // Upstream stream ready
    this.socket.on("voice:stream-ready", (data) => {
      console.log("[StreamingVoiceClient] Voice stream ready on server:", data?.streamId);
    });

    // Sarvam VAD Speech Start -> Barge-in trigger!
    this.socket.on("voice:speech-start", (data) => {
      console.log("[StreamingVoiceClient] 🎙️ Speech start detected by Sarvam VAD:", data?.streamId);
      // Immediately stop any active TTS playback
      this.stopPlayback();
      this.hasSpoken = true;
      this.lastSpokenTimestamp = Date.now();
      this.speechStartTime = this.speechStartTime || Date.now();
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      if (this.partialSilenceTimer) {
        clearTimeout(this.partialSilenceTimer);
        this.partialSilenceTimer = null;
      }
      this.onSpeechStart();
      this.onStateChange("LISTENING");
    });

    // Sarvam Realtime Partial Transcript (Interim feedback as user speaks)
    this.socket.on("voice:transcript-partial", (data) => {
      const partialText = (data?.transcript || "").trim();
      if (partialText) {
        this.hasSpoken = true;
        this.lastSpokenTimestamp = Date.now();
        this.latestPartialText = partialText;
        if (this.silenceTimer) {
          clearTimeout(this.silenceTimer);
          this.silenceTimer = null;
        }
        this.onTranscriptPartial(partialText);

        // Responsive cutoff: if user stops speaking after words arrived,
        // wait only 300ms of quiet before auto-submitting the command!
        if (this.partialSilenceTimer) {
          clearTimeout(this.partialSilenceTimer);
        }
        this.partialSilenceTimer = setTimeout(() => {
          if (this.isStreaming) {
            console.log("[StreamingVoiceClient] ⚡ 300ms quiet after partial transcript: Auto-submitting!");
            this.stopRecordingCommand();
          }
        }, 300);
      }
    });

    // Sarvam VAD Speech End -> auto-finalize and submit immediately
    this.socket.on("voice:speech-end", (data) => {
      console.log("[StreamingVoiceClient] 🛑 Speech end detected by Sarvam VAD:", data?.streamId);
      this.onSpeechEnd();
      if (this.isStreaming) {
        console.log("[StreamingVoiceClient] ⚡ Server VAD speech end: Auto-submitting command!");
        this.stopRecordingCommand();
      }
    });

    // Sarvam Realtime Final Transcript
    this.socket.on("voice:transcript-final", (data) => {
      const finalText = (data?.transcript || "").trim();
      console.log("[StreamingVoiceClient] ✅ Final transcript from Sarvam:", finalText);
      if (finalText) {
        this.latestPartialText = finalText;
      }
      this.onTranscriptFinal(finalText);
      
      this.onStateChange("UNDERSTANDING");
      
      if (this.isStreaming) {
        this.stopRecordingCommand(false);
      } else {
        this._stopMicTracks();
      }
    });

    // Final result from server (intent, feedback, synthesized audio)
    this.socket.on("voice:result", (result) => {
      this._handleServerResult(result);
    });
  }

  /**
   * Initializes the Web Audio pipeline on demand: AudioContext + AudioWorklet + Analyser
   */
  async initAudio() {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!this.audioContext || this.audioContext.state === "closed") {
        try {
          this.audioContext = new AudioCtx({ sampleRate: 16000 });
        } catch (sampleRateErr) {
          // Mobile Safari & certain Android browsers reject custom sampleRate; fallback to hardware rate
          this.audioContext = new AudioCtx();
        }
      }
      if (this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
        } catch (e) {}
      }

      // 1. Create AnalyserNode (FFT size 256 for smooth real-time 60fps rendering)
      if (!this.analyserNode) {
        this.analyserNode = this.audioContext.createAnalyser();
        this.analyserNode.fftSize = 256;
        this.analyserNode.smoothingTimeConstant = 0.45;
        this.fftDataArray = new Uint8Array(this.analyserNode.frequencyBinCount);
        this.timeDomainArray = new Uint8Array(this.analyserNode.frequencyBinCount);
      }

      // 2. Load AudioWorkletProcessor for 16kHz PCM capture with ScriptProcessor fallback for mobile
      let workletReady = false;
      if (this.audioContext.audioWorklet && !this.workletNode) {
        try {
          const blob = new Blob([PCM_PROCESSOR_CODE], { type: "application/javascript" });
          const workletUrl = URL.createObjectURL(blob);
          try {
            await this.audioContext.audioWorklet.addModule(workletUrl);
            this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-recorder-processor");
            this.workletNode.port.onmessage = (event) => {
              if (event.data && event.data.event === "pcm-chunk") {
                this._handlePCMChunk(event.data.buffer);
              }
            };
            workletReady = true;
          } finally {
            URL.revokeObjectURL(workletUrl);
          }
        } catch (workletErr) {
          console.warn("[StreamingVoiceClient] AudioWorklet failed on mobile/browser; using ScriptProcessor fallback:", workletErr.message);
        }
      } else if (this.workletNode) {
        workletReady = true;
      }

      // Fallback: ScriptProcessorNode (universal support across iOS Safari & Android WebViews)
      if (!workletReady && !this.scriptProcessorNode) {
        const bufferSize = 2048;
        this.scriptProcessorNode = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
        const inputRate = this.audioContext.sampleRate;
        const targetRate = 16000;
        const ratio = inputRate / targetRate;
        let resampleOffset = 0;

        this.scriptProcessorNode.onaudioprocess = (e) => {
          if (!this.isStreaming) return;
          const inputData = e.inputBuffer.getChannelData(0);
          const inputLen = inputData.length;
          const outSamples = [];

          if (inputRate === targetRate) {
            for (let i = 0; i < inputLen; i++) {
              const s = Math.max(-1, Math.min(1, inputData[i]));
              outSamples.push(s < 0 ? s * 0x8000 : s * 0x7fff);
            }
          } else {
            while (resampleOffset < inputLen) {
              const idx = Math.floor(resampleOffset);
              const frac = resampleOffset - idx;
              const s0 = inputData[idx];
              const s1 = (idx + 1 < inputLen) ? inputData[idx + 1] : s0;
              const interpolated = s0 + frac * (s1 - s0);
              const s = Math.max(-1, Math.min(1, interpolated));
              outSamples.push(s < 0 ? s * 0x8000 : s * 0x7fff);
              resampleOffset += ratio;
            }
            resampleOffset -= inputLen;
          }

          if (outSamples.length > 0) {
            const chunk = new Int16Array(outSamples);
            this._handlePCMChunk(chunk.buffer);
          }
        };
      }

      // 3. Acquire real device microphone stream
      const hasLiveTracks = this.mediaStream && this.mediaStream.getTracks().some((t) => t.readyState === "live");
      if (!hasLiveTracks) {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1
          }
        });

        if (this.sourceNode) {
          try { this.sourceNode.disconnect(); } catch (e) {}
        }
        this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.sourceNode.connect(this.analyserNode);

        if (this.workletNode) {
          this.sourceNode.connect(this.workletNode);
        } else if (this.scriptProcessorNode) {
          this.sourceNode.connect(this.scriptProcessorNode);
          if (!this.muteGainNode) {
            this.muteGainNode = this.audioContext.createGain();
            this.muteGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            this.muteGainNode.connect(this.audioContext.destination);
          }
          this.scriptProcessorNode.connect(this.muteGainNode);
        }
      }

      // 4. Start volume/metering loop
      this._startMeteringLoop();

      return true;
    } catch (err) {
      console.error("[StreamingVoiceClient] Audio initialization failed:", err);
      let errorDetail = err;
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorDetail = {
          type: "PERMISSION_DENIED",
          message: "Microphone access denied. Please allow microphone permission in your browser settings."
        };
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorDetail = {
          type: "NO_MIC_FOUND",
          message: "No microphone detected on your device."
        };
      }
      this.onError(errorDetail);
      this.onStateChange("IDLE");
      return false;
    }
  }

  /**
   * Processes raw PCM chunk: streams directly to backend gateway over WebSocket
   */
  _handlePCMChunk(arrayBuffer) {
    if (this.isStreaming && this.socket) {
      this.socket.emit("voice:audio-chunk", arrayBuffer);
    }
  }

  /**
   * Starts active microphone recording & streaming to Sarvam Realtime STT
   */
  async startRecordingCommand(options = {}) {
    // Barge-in: if currently speaking, stop playback immediately
    this.stopPlayback();

    this.onStateChange("ACTIVATING");

    this.currentStreamId = `stream-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const lang = options.language || this.language || "auto";

    // ⚡ CONCURRENT STARTUP: Notify backend voice gateway immediately so Sarvam WS connects concurrently!
    if (this.socket) {
      this.socket.emit("voice:start-stream", {
        streamId: this.currentStreamId,
        language: lang,
        sampleRate: 16000,
        userName: options.userName || "Farmer"
      });
    }

    const ok = await this.initAudio();
    if (!ok) return false;

    this.isStreaming = true;
    this.hasSpoken = false;
    this.ambientNoise = 0.006;
    this.lastSpokenTimestamp = 0;
    this.speechStartTime = Date.now();
    this.latestPartialText = "";
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.partialSilenceTimer) {
      clearTimeout(this.partialSilenceTimer);
      this.partialSilenceTimer = null;
    }

    if (this.workletNode) {
      this.workletNode.port.postMessage({ command: "start" });
    }

    this.onStateChange("LISTENING");
    this._playChime();

    // Safety fallback timer (12 seconds)
    if (this.maxRecordingTimer) clearTimeout(this.maxRecordingTimer);
    this.maxRecordingTimer = setTimeout(() => {
      if (this.isStreaming) {
        console.log("[StreamingVoiceClient] Max recording duration safety reached.");
        this.stopRecordingCommand();
      }
    }, 12000);

    return true;
  }

  /**
   * Stops recording and finalizes stream to backend
   */
  stopRecordingCommand(transitionToTranscribing = true) {
    if (!this.isStreaming) return;

    if (this.maxRecordingTimer) {
      clearTimeout(this.maxRecordingTimer);
      this.maxRecordingTimer = null;
    }

    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }

    if (this.partialSilenceTimer) {
      clearTimeout(this.partialSilenceTimer);
      this.partialSilenceTimer = null;
    }

    this.isStreaming = false;

    if (this.workletNode) {
      this.workletNode.port.postMessage({ command: "stop" });
    }

    // Stop microphone tracks immediately to save battery and prevent leaks
    this._stopMicTracks();

    // Transition state to TRANSCRIBING immediately
    if (transitionToTranscribing) {
      this.onStateChange("TRANSCRIBING");
    }

    // Signal end of stream to backend with latest known transcript + silence tail for Sarvam VAD
    if (this.socket) {
      try {
        // Send a 300ms silence tail to guarantee Sarvam VAD speech_end detection
        const silenceTail = new Int16Array(4800);
        this.socket.emit("voice:audio-chunk", silenceTail.buffer);
      } catch (e) {}

      this.socket.emit("voice:end-stream", {
        streamId: this.currentStreamId,
        clientTranscript: this.latestPartialText || ""
      });
    }
  }

  /**
   * Stop active microphone tracks
   */
  _stopMicTracks() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        try { track.stop(); } catch (e) {}
      });
      this.mediaStream = null;
    }
  }

  /**
   * Immediate Barge-in / Interrupt: Stops any playing TTS speech immediately
   */
  stopPlayback() {
    if (this.playbackSourceNode) {
      try {
        this.playbackSourceNode.stop();
        this.playbackSourceNode.disconnect();
      } catch (e) {}
      this.playbackSourceNode = null;
    }
    if (this.socket && this.isConnected) {
      this.socket.emit("voice:interrupt");
    }
  }

  /**
   * Cancels any active voice interaction
   */
  cancel() {
    this.stopPlayback();
    this.isStreaming = false;

    if (this.maxRecordingTimer) {
      clearTimeout(this.maxRecordingTimer);
      this.maxRecordingTimer = null;
    }

    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }

    if (this.partialSilenceTimer) {
      clearTimeout(this.partialSilenceTimer);
      this.partialSilenceTimer = null;
    }

    if (this.workletNode) {
      this.workletNode.port.postMessage({ command: "stop" });
    }

    this._stopMicTracks();

    if (this.socket && this.isConnected) {
      this.socket.emit("voice:cancel-stream");
    }

    this.onStateChange("IDLE");
  }

  /**
   * Handles final response from the backend: transcripts, intent, and synthesized audio
   */
  async _handleServerResult(result) {
    console.log("[StreamingVoiceClient] Received server result:", result);

    // Stop mic if not already stopped
    this._stopMicTracks();

    // Stop any active previous TTS playback before starting new one
    this.stopPlayback();

    if (!result || !result.success) {
      this.onResult(result);
      // Removed this.onStateChange("IDLE") to allow the provider's 2-second error timer to display the error
      return;
    }

    // Forward result to provider for action execution
    this.onResult(result);

    // If backend returned synthesized speech audio, play it via Web Audio API
    if (result.audioBase64) {
      this.onStateChange("SPEAKING");
      await this._playSynthesizedAudio(result.audioBase64, () => {
        this.onStateChange("IDLE");
      });
    } else {
      this.onStateChange("IDLE");
    }
  }

  /**
   * Plays synthesized server audio via Web Audio API & routes it to AnalyserNode
   * so the UI orb reacts in real time to assistant voice!
   */
  async _playSynthesizedAudio(base64Data, onEnded) {
    try {
      if (!this.audioContext || this.audioContext.state === "closed") {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioCtx();
      }
      if (this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
        } catch (e) {}
      }

      // Convert Base64 string to ArrayBuffer
      const binaryString = atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBuffer = await this.audioContext.decodeAudioData(bytes.buffer);

      // Stop previous playback if active
      this.stopPlayback();

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Connect playback to AnalyserNode for visualizer synchronization, then to speakers
      if (this.analyserNode) {
        source.connect(this.analyserNode);
      }
      source.connect(this.audioContext.destination);

      source.onended = () => {
        this.playbackSourceNode = null;
        if (typeof onEnded === "function") onEnded();
      };

      this.playbackSourceNode = source;
      source.start(0);
    } catch (err) {
      console.warn("[StreamingVoiceClient] Web Audio playback error:", err);
      if (typeof onEnded === "function") onEnded();
    }
  }

  /**
   * Synthesizes subtle activation chime
   */
  _playChime() {
    try {
      if (!this.audioContext || this.audioContext.state === "closed") return;
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, this.audioContext.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, this.audioContext.currentTime + 0.16); // A5

      gain.gain.setValueAtTime(0.08, this.audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.22);

      osc.connect(gain);
      gain.connect(this.audioContext.destination);

      osc.start();
      osc.stop(this.audioContext.currentTime + 0.22);
    } catch (e) {}
  }

  /**
   * Metering loop for volume and visualizer frequencies + Client-side Low-Latency Energy VAD
   */
  _startMeteringLoop() {
    const update = () => {
      if (this.analyserNode && this.fftDataArray && this.timeDomainArray) {
        this.analyserNode.getByteFrequencyData(this.fftDataArray);
        this.analyserNode.getByteTimeDomainData(this.timeDomainArray);

        // 1. Time-domain waveform amplitude (deviation from 128)
        let rms = 0;
        let peakDev = 0;
        const len = this.timeDomainArray.length;
        for (let i = 0; i < len; i++) {
          const dev = (this.timeDomainArray[i] - 128) / 128.0;
          if (Math.abs(dev) > peakDev) peakDev = Math.abs(dev);
          rms += dev * dev;
        }
        rms = Math.sqrt(rms / len);
        
        // Calculate decibels (dB)
        let db = 20 * Math.log10(rms);
        if (!isFinite(db)) db = -100; // Silence

        // Frequency-domain normalized volume for UI
        let freqSum = 0;
        const bins = this.fftDataArray.length;
        for (let i = 0; i < bins; i++) {
          freqSum += this.fftDataArray[i];
        }
        const freqVolume = freqSum / (bins * 255);

        // Update UI Visualizer
        this.onVolumeChange(Math.max(rms * 2.0, freqVolume), this.fftDataArray, this.timeDomainArray);

        // 3. Standard Client-side dB VAD
        if (this.isStreaming) {
          // A standard speaking threshold is around -45dB to -35dB depending on the mic.
          const isSpeakingNow = db > -40;

          if (isSpeakingNow) {
            this.hasSpoken = true;
            this.lastSpokenTimestamp = Date.now();
          } else if (this.hasSpoken && this.lastSpokenTimestamp > 0) {
            // User spoke and has now stopped speaking
            const elapsedSilence = Date.now() - this.lastSpokenTimestamp;
            // 1.0 second of silence -> auto-submit!
            if (elapsedSilence >= 1000) {
              console.log(`[StreamingVoiceClient] ⚡ User stopped speaking (1s pause). dB=${db.toFixed(1)}. Auto-submitting!`);
              this.stopRecordingCommand();
            }
          } else {
             // If user hasn't spoken at all for 1.5 seconds, auto stop (prevent hanging open)
             if (this.speechStartTime && (Date.now() - this.speechStartTime) > 1300 && !this.hasSpoken) {
                console.log(`[StreamingVoiceClient] ⚡ Absolute silence for 1.5s. dB=${db.toFixed(1)}. Auto-submitting!`);
                this.stopRecordingCommand();
             }
          }
        }
      }

      this.animFrameId = requestAnimationFrame(update);
    };

    if (!this.animFrameId) {
      this.animFrameId = requestAnimationFrame(update);
    }
  }

  destroy() {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.maxRecordingTimer) clearTimeout(this.maxRecordingTimer);
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    if (this.partialSilenceTimer) clearTimeout(this.partialSilenceTimer);

    this.stopPlayback();
    this._stopMicTracks();

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {});
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }
}

export default StreamingVoiceClient;
